/**
 * 定向券（targeting_coupon）全局 Context。
 * 在 _app 挂载，供商品卡展示/领取；规格见 @doc_id store_personalized_coupon
 *
 * 拉取策略（/ec/growth/benefits/query）：
 * - 仅已登录用户请求
 * - 登录成功 / 冷启动已登录：拉 1 次
 * - 回前台：visible 回调 500ms 防抖后拉取
 * - 领券成功 / refreshPersonalizedCoupons：立即刷新
 * - 不因路由或列表 query 变化重复拉取
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { callAppFunction, isSayWeeeApp } from '@weee-fe/utilities';
import { claimCouponLandingProduct } from '@/api/promotion';
import { getTargetCouponInfo } from '@/api/growth';
import { TargetCouponData } from '@/types/coupon';
import useAccount from '@/store/account';
import usePageVisible from '@/hooks/usePageVisible';
import useDebounce from '@/hooks/debounce';
import { normalizeProductId, pickTargetingCoupon, shouldFetchOnLoginTransition } from './fetch-policy';

interface PersonalizedCouponContextType {
  /** 当前用户的定向券数据（含各商品 discounts） */
  personalizedCouponData: TargetCouponData | null;
  loading: boolean;
  /** 手动刷新，用于其它用券成功等场景 */
  refreshPersonalizedCoupons: () => Promise<void>;
  /** 领取指定商品的定向券 */
  claimPersonalizedCoupon: (productId: number) => Promise<void>;
}

const PAGE_VISIBLE_FETCH_DEBOUNCE_MS = 500;

const PersonalizedCouponContext = createContext<PersonalizedCouponContextType | undefined>(undefined);

/** @doc_id store_personalized_coupon */
export const PersonalizedCouponProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLogin } = useAccount();
  const [personalizedCouponData, setPersonalizedCouponData] = useState<TargetCouponData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  /** 上一轮 isLogin，用于识别 false→true */
  const prevLoginRef = useRef<boolean | undefined>(undefined);
  /** 与 isLogin 同步，供异步回调判断登出竞态 */
  const isLoginRef = useRef(isLogin);
  /** 进行中的请求，避免并发重复打接口 */
  const inflightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    isLoginRef.current = isLogin;
  }, [isLogin]);

  const fetchCouponStatus = useCallback(async () => {
    if (!isLoginRef.current) {
      return;
    }

    if (inflightRef.current) {
      return inflightRef.current;
    }

    setLoading(true);
    const request = (async () => {
      try {
        const res = await getTargetCouponInfo(['targeting_coupon']);
        if (!isLoginRef.current) {
          return;
        }

        if (res?.result) {
          const targetingCoupons = pickTargetingCoupon(res?.object?.targetingCoupons);
          setPersonalizedCouponData(targetingCoupons);
        }
      } catch (error) {
        console.error('Fetch Error', error);
      } finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();

    inflightRef.current = request;
    return request;
  }, []);

  // 登录态：未登录清空且不请求；登录成功或冷启动已登录时拉取一次
  useEffect(() => {
    if (!isLogin) {
      setPersonalizedCouponData(null);
      prevLoginRef.current = isLogin;
      return;
    }

    if (shouldFetchOnLoginTransition(prevLoginRef.current, isLogin)) {
      fetchCouponStatus();
    }

    prevLoginRef.current = isLogin;
  }, [isLogin, fetchCouponStatus]);

  const handlePageVisible = useDebounce(
    () => {
      fetchCouponStatus();
    },
    PAGE_VISIBLE_FETCH_DEBOUNCE_MS,
    [fetchCouponStatus]
  );

  usePageVisible(handlePageVisible);

  const claimPersonalizedCoupon = useCallback(
    async (productId: number) => {
      if (!personalizedCouponData) return;
      const idNum = normalizeProductId(productId);
      if (idNum == null) return;

      const previousData = { ...personalizedCouponData };

      setPersonalizedCouponData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          discounts: prev.discounts.map(item => (Number(item.product_id) === idNum ? { ...item, claimed: true } : item))
        };
      });
      try {
        const res = await claimCouponLandingProduct({ product_id: idNum });
        if (res.result) {
          if (isSayWeeeApp()) {
            callAppFunction('refreshNativePage');
          }
          await fetchCouponStatus();
        } else {
          throw new Error('Claim failed');
        }
      } catch (error) {
        if (isLoginRef.current) {
          setPersonalizedCouponData(previousData);
        }
      }
    },
    [fetchCouponStatus, personalizedCouponData]
  );

  const refreshPersonalizedCoupons = useCallback(() => fetchCouponStatus(), [fetchCouponStatus]);

  const contextValue = useMemo(
    () => ({
      personalizedCouponData,
      loading,
      refreshPersonalizedCoupons,
      claimPersonalizedCoupon
    }),
    [personalizedCouponData, loading, refreshPersonalizedCoupons, claimPersonalizedCoupon]
  );

  return <PersonalizedCouponContext.Provider value={contextValue}>{children}</PersonalizedCouponContext.Provider>;
};

/**
 * 空实现 Provider：不拉券、不监听可见性。
 * 用于 CMS 预览等不需要定向券能力的场景。
 */
export const PersonalizedCouponStubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const contextValue = useMemo<PersonalizedCouponContextType>(
    () => ({
      personalizedCouponData: null,
      loading: false,
      refreshPersonalizedCoupons: async () => {},
      claimPersonalizedCoupon: async () => {}
    }),
    []
  );

  return <PersonalizedCouponContext.Provider value={contextValue}>{children}</PersonalizedCouponContext.Provider>;
};

export const usePersonalizedCoupon = () => {
  const context = useContext(PersonalizedCouponContext);
  if (!context) throw new Error('usePersonalizedCoupon must be used within a PersonalizedCouponProvider');

  return {
    ...context
  };
};
